<template>
  <div class="setup-wizard-page">
    <!-- Top Progress Banner -->
    <div class="wizard-header-banner">
      <div class="banner-content">
        <div class="banner-left">
          <div class="banner-icon-wrap">
            <feather-icon icon="ZapIcon" size="24"/>
          </div>
          <div>
            <h2 class="banner-title">{{ msg('Branch Setup Wizard') }}</h2>
            <p class="banner-subtitle" v-if="branchData">{{ branchData.name || msg('New Branch') }}</p>
          </div>
        </div>
        <div class="banner-right">
          <div v-if="allBranches.length > 1" class="branch-picker-wrap">
            <label class="branch-picker-label">{{ msg('Branch') }}</label>
            <v-select
                :value="branchId"
                :options="allBranches"
                label="name"
                :reduce="b => b.id"
                :clearable="false"
                class="branch-picker-select"
                @input="onBranchChange"
            />
          </div>
          <div class="completion-ring">
            <svg viewBox="0 0 36 36" class="circular-chart">
              <path class="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
              <path class="circle" :stroke-dasharray="`${completionPercentage}, 100`"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
              <text x="18" y="20.35" class="percentage">{{ completionPercentage }}%</text>
            </svg>
          </div>
          <span class="completion-label">{{ msg('Complete') }}</span>
        </div>
      </div>
    </div>

    <div class="wizard-body" v-if="!loading">
      <div class="wizard-layout">
        <!-- Left Sidebar: Checklist -->
        <div class="wizard-sidebar">
          <div class="sidebar-card">
            <div class="sidebar-header">
              <feather-icon icon="CheckSquareIcon" size="18"/>
              <h5>{{ msg('Setup Checklist') }}</h5>
            </div>
            <div class="checklist-items">
              <div v-for="item in checklist" :key="item.id"
                   class="checklist-item"
                   :class="{ 'completed': item.completed, 'active': currentStep === item.step, 'locked': item.step > maxReachableStep && item.step !== steps.length - 1 }"
                   @click="jumpToStep(item.step)">
                <div class="check-indicator">
                  <feather-icon v-if="item.completed" icon="CheckCircleIcon" size="18" class="check-done"/>
                  <feather-icon v-else icon="CircleIcon" size="18" class="check-pending"/>
                </div>
                <div class="check-content">
                  <span class="check-label">{{ msg(item.label) }}</span>
                  <span class="check-desc">{{ item.description }}</span>
                </div>
                <feather-icon icon="ChevronRightIcon" size="14" class="check-arrow"/>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Main: Step Content -->
        <div class="wizard-main">
          <!-- Step Navigation Tabs -->
          <div class="step-tabs">
            <div v-for="(step, index) in steps" :key="index"
                 class="step-tab"
                 :class="{ 'active': currentStep === index, 'completed': isStepCompleted(index), 'locked': index > maxReachableStep && index !== steps.length - 1 }"
                 @click="jumpToStep(index)">
              <div class="step-number">
                <feather-icon v-if="isStepCompleted(index)" icon="CheckIcon" size="14"/>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ msg(step.label) }}</span>
            </div>
          </div>

          <!-- Step Content Area -->
          <div class="step-content-area">
            <transition name="slide-fade" mode="out-in">
              <!-- Step 0: Branch Info -->
              <div v-if="currentStep === 0" key="branch-info" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="BuildingIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Branch Information') }}</h4>
                    <p>{{ msg('Fill in your basic company details to get started') }}</p>
                  </div>
                </div>
                <div class="step-form" v-if="branchData">
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Branch Name')" label-for="wiz-name">
                        <b-form-input id="wiz-name" v-model="branchData.name" :placeholder="msg('e.g. Downtown Office')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Contact Full Name')" label-for="wiz-contact">
                        <b-form-input id="wiz-contact" v-model="branchData.contact" :placeholder="msg('e.g. John Doe')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Email')" label-for="wiz-email">
                        <b-input-group>
                          <b-input-group-prepend is-text>
                            <feather-icon icon="MailIcon" size="16"/>
                          </b-input-group-prepend>
                          <b-form-input id="wiz-email" v-model="branchData.email" type="email" :placeholder="msg('email@company.com')"/>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Phone')" label-for="wiz-phone">
                        <b-input-group>
                          <b-input-group-prepend is-text>
                            <feather-icon icon="PhoneIcon" size="16"/>
                          </b-input-group-prepend>
                          <b-form-input id="wiz-phone" v-model="branchData.phone" :placeholder="msg('+43 123 456 789')"/>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="8">
                      <b-form-group :label="msg('Address')" label-for="wiz-address">
                        <b-input-group>
                          <b-input-group-prepend is-text>
                            <feather-icon icon="MapPinIcon" size="16"/>
                          </b-input-group-prepend>
                          <b-form-input id="wiz-address" v-model="branchData.address" :placeholder="msg('Street, City, ZIP')"/>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                      <b-form-group :label="msg('Currency')" label-for="wiz-currency">
                        <v-select
                            v-model="branchData.cur"
                            :options="currencyOptions"
                            :reduce="c => c.value"
                            :clearable="false"
                            class="w-100"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Website')" label-for="wiz-website">
                        <b-input-group>
                          <b-input-group-prepend is-text>
                            <feather-icon icon="GlobeIcon" size="16"/>
                          </b-input-group-prepend>
                          <b-form-input id="wiz-website" v-model="branchData.website" :placeholder="msg('https://yourwebsite.com')"/>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="3">
                      <b-form-group :label="msg('Management Fee (%)')" label-for="wiz-fee">
                        <b-form-input id="wiz-fee" type="number" step="0.5" v-model="branchData.managementFee"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="3">
                      <b-form-group :label="msg('Unit Price (EUR)')" label-for="wiz-unit-price">
                        <b-form-input id="wiz-unit-price" type="number" step="0.5" v-model="branchData.unitPrice"/>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </div>

              <!-- Step 1: Create Owner Client -->
              <div v-else-if="currentStep === 1" key="owner-client" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="UserIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Create Owner Client') }}</h4>
                    <p>{{ msg('Add a client marked as building owner. This client will be available when creating buildings.') }}</p>
                  </div>
                </div>
                <div class="step-form">
                  <b-alert show variant="info" class="mb-3">
                    <feather-icon icon="InfoIcon" size="18" class="mr-1"/>
                    {{ msg('Create a client with owner role.') }}
                  </b-alert>
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('First name')" label-for="owner-firstname" :state="!!ownerClient.firstname">
                        <b-form-input id="owner-firstname" v-model="ownerClient.firstname" :placeholder="msg('First name')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Last name')" label-for="owner-lastname" :state="!!ownerClient.lastname">
                        <b-form-input id="owner-lastname" v-model="ownerClient.lastname" :placeholder="msg('Last name')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Email')" label-for="owner-email">
                        <b-form-input id="owner-email" v-model="ownerClient.email" type="email" :placeholder="msg('email@example.com')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Phone')" label-for="owner-phone" :state="!!(ownerClient.phone || ownerClient.mobile)">
                        <b-form-input id="owner-phone" v-model="ownerClient.phone" :placeholder="msg('Phone or mobile')"/>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <div class="action-buttons mt-2">
                    <b-button variant="primary" @click="createOwnerClient" :disabled="creatingOwner || !canCreateOwnerClient">
                      <b-spinner small v-if="creatingOwner" class="mr-50"/>
                      <feather-icon v-else icon="UserPlusIcon" size="16" class="mr-50"/>
                      {{ msg('Create Owner Client') }}
                    </b-button>
                    <b-button variant="outline-secondary" class="ml-1" @click="refreshStatus" :disabled="creatingOwner">
                      <feather-icon icon="RefreshCwIcon" size="16" class="mr-50"/>
                      {{ msg('Refresh Status') }}
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Step 2: First Building -->
              <div v-else-if="currentStep === 2" key="first-building" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="HomeIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Create First Building') }}</h4>
                    <p>{{ msg('Stripe connection is per building. Create your first building before connecting Stripe.') }}</p>
                  </div>
                </div>
                <div class="step-form">
                  <b-alert show variant="info" class="mb-3">
                    <feather-icon icon="InfoIcon" size="18" class="mr-1"/>
                    {{ msg('Select the owner (created in previous step) and fill in the building details. The building will be saved to your CRM.') }}
                  </b-alert>
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Owner')" label-for="first-building-owner" :state="!!firstBuilding.ownerId">
                        <client-picker v-model="firstBuilding.ownerId" is-owner :clearable="false"
              :no-results-message="msg('No owner clients yet. Please create one in the previous step first.')"/>
                        <small class="text-muted">{{ msg('Select the building owner (required). Create one in the previous step if needed.') }}</small>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row v-if="allBranches.length > 1">
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Branch')" label-for="first-building-branch">
                        <v-select
                            :value="branchId"
                            :options="allBranches"
                            label="name"
                            :reduce="b => b.id"
                            class="w-100"
                            :dir="isRTL ? 'rtl' : 'ltr'"
                            @input="onBranchChange"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Company')" label-for="first-building-company">
                        <b-form-input id="first-building-company" v-model="firstBuilding.company" :placeholder="msg('Company name')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group :label="msg('Address')" label-for="first-building-address">
                        <b-form-input id="first-building-address" v-model="firstBuilding.address" :placeholder="msg('Street address')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                      <b-form-group :label="msg('Zip')" label-for="first-building-zip">
                        <b-form-input id="first-building-zip" v-model="firstBuilding.zip" :placeholder="msg('ZIP code')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                      <b-form-group :label="msg('Location')" label-for="first-building-location">
                        <b-form-input id="first-building-location" v-model="firstBuilding.location" :placeholder="msg('City/Location')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                      <b-form-group :label="msg('GST')" label-for="first-building-gst">
                        <b-form-input id="first-building-gst" v-model="firstBuilding.gst" :placeholder="msg('GST number')"/>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                      <b-form-group :label="msg('KG')" label-for="first-building-kg">
                        <b-form-input id="first-building-kg" v-model="firstBuilding.kg" :placeholder="msg('KG number')"/>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <div class="action-buttons mt-2">
                    <b-button variant="primary" @click="createFirstBuilding" :disabled="creatingBuilding || !canCreateFirstBuilding">
                      <b-spinner small v-if="creatingBuilding" class="mr-50"/>
                      <feather-icon v-else icon="PlusIcon" size="16" class="mr-50"/>
                      {{ msg('Create Building') }}
                    </b-button>
                    <b-button variant="outline-secondary" class="ml-1" @click="refreshStatus" :disabled="creatingBuilding">
                      <feather-icon icon="RefreshCwIcon" size="16" class="mr-50"/>
                      {{ msg('Refresh Status') }}
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Step 3: Check-in agreement (per building, post-booking RENT_OFFER) -->
              <div v-else-if="currentStep === 3" key="offer-templates" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="FileTextIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Check-in Agreement') }}</h4>
                    <p>{{ msg('Set up the agreement guests must sign before checking in. Each building can have its own template(s).') }}</p>
                  </div>
                </div>
                <div class="step-form">
                  <b-alert v-if="!hasBuildings" show variant="warning" class="mb-2">
                    <feather-icon icon="AlertCircleIcon" size="18" class="mr-1"/>
                    {{ msg('Create your first building in the previous step before assigning check-in agreements.') }}
                  </b-alert>
                  <template v-else>
                    <b-alert show variant="info" class="mb-2">
                      <feather-icon icon="InfoIcon" size="18" class="mr-1"/>
                      {{ msg('Choose a check-in agreement template from your library or create a new one with placeholders. Guests will sign this after booking, before they are allowed to check in. Save applies all rows below.') }}
                    </b-alert>
                    <div class="d-flex flex-wrap align-items-end mb-2" style="gap:10px">
                      <div class="flex-grow-1" style="min-width:220px">
                        <label class="small text-muted mb-25 d-block">{{ msg('Apply one template to all buildings') }}</label>
                        <v-select
                            v-model="bulkOfferTemplateId"
                            :options="rentOfferTemplates"
                            label="name"
                            :reduce="t => t.id"
                            :clearable="true"
                            :placeholder="msg('Select template')"
                            class="w-100"
                        />
                      </div>
                      <b-button variant="outline-primary" :disabled="!bulkOfferTemplateId" @click="applyBulkOfferTemplate">
                        {{ msg('Apply to all') }}
                      </b-button>
                      <b-button variant="flat-secondary" @click="loadOfferStepData" :disabled="offerStepLoading">
                        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50"/>
                        {{ msg('Reload') }}
                      </b-button>
                      <b-button variant="outline-secondary" @click="goToNewOfferTemplate">
                        <feather-icon icon="PlusIcon" size="16" class="mr-50"/>
                        {{ msg('New template') }}
                      </b-button>
                      <b-button variant="outline-secondary" @click="goToOfferTemplatesList">
                        <feather-icon icon="ExternalLinkIcon" size="16" class="mr-50"/>
                        {{ msg('All templates') }}
                      </b-button>
                    </div>
                    <b-alert v-if="!offerStepLoading && rentOfferTemplates.length === 0" show variant="warning" class="mb-2">
                      {{ msg('No check-in agreement templates found for this branch. Create one (with placeholders) in Contract Templates.') }}
                    </b-alert>
                    <div v-if="offerStepLoading" class="text-center py-2">
                      <b-spinner small/>
                    </div>
                    <div v-else class="offer-building-list">
                      <div v-for="b in wizardBuildings" :key="b.id" class="offer-building-row mb-1 p-1 border rounded">
                        <div class="font-weight-bold mb-50">{{ b.company || b.address || ('#' + b.id) }}</div>
                        <small class="text-muted d-block mb-50">{{ b.address }} {{ b.zip }} {{ b.location }}</small>
                        <v-select
                            v-model="buildingOfferSelections[b.id]"
                            :options="rentOfferTemplates"
                            label="name"
                            :reduce="t => t.id"
                            multiple
                            :close-on-select="false"
                            :placeholder="msg('Check-in agreements')"
                            class="w-100"
                        />
                      </div>
                    </div>
                    <div class="action-buttons mt-2">
                      <b-button variant="primary" @click="saveOfferTemplateAssignments" :disabled="savingOffers || offerStepLoading || wizardBuildings.length === 0">
                        <b-spinner v-if="savingOffers" small class="mr-50"/>
                        <feather-icon v-else icon="SaveIcon" size="16" class="mr-50"/>
                        {{ msg('Save assignments') }}
                      </b-button>
                      <b-button variant="outline-secondary" class="ml-1" @click="refreshStatus" :disabled="savingOffers">
                        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50"/>
                        {{ msg('Refresh Status') }}
                      </b-button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Step 4: Channel Manager -->
              <div v-else-if="currentStep === 4" key="channel-manager" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="LinkIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Channel Manager') }}</h4>
                    <p>{{ msg('Connect your channel manager (Hostaway, Apaleo, etc.)') }}</p>
                  </div>
                </div>
                <div class="step-form" v-if="branchData">
                  <div class="form-section">
                    <div class="section-title">
                      <feather-icon icon="ServerIcon" size="18"/>
                      <h5>{{ msg('Channel Manager') }}</h5>
                    </div>
                    <b-row>
                      <b-col cols="12" md="4">
                        <b-form-group :label="msg('External API Type')">
                          <v-select
                              v-model="branchData.externalApiType"
                              :options="['HOSTAWAY', 'RENTAL', 'APALEO', 'CLOUDBEDS']"
                              :clearable="true"
                              :placeholder="msg('Select provider')"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" md="4">
                        <b-form-group :label="msg('Channel ID')">
                          <b-form-input v-model="branchData.channelId" :placeholder="msg('Your Channel ID')"/>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" md="4">
                        <b-form-group :label="msg('Channel Secret')">
                          <b-input-group>
                            <b-form-input :type="showChannelSecret ? 'text' : 'password'" v-model="branchData.channelSecret" :placeholder="msg('Your Channel Secret')"/>
                            <b-input-group-append is-text>
                              <feather-icon :icon="showChannelSecret ? 'EyeOffIcon' : 'EyeIcon'" size="16" class="cursor-pointer" @click="showChannelSecret = !showChannelSecret"/>
                            </b-input-group-append>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                  <div class="form-section">
                    <div class="section-title">
                      <feather-icon icon="KeyIcon" size="18"/>
                      <h5>{{ msg('KeyBox Provider') }}</h5>
                    </div>
                    <b-row>
                      <b-col cols="12" md="4">
                        <b-form-group :label="msg('KeyBox Provider')">
                          <v-select
                              v-model="branchExtData.keyBoxProvider"
                              :options="['Quipeng', 'Keynest']"
                              :clearable="true"
                              :placeholder="msg('Select provider')"
                          />
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </div>

              <!-- Step 5: Stripe -->
              <div v-else-if="currentStep === 5" key="stripe" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="CreditCardIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Stripe Payment') }}</h4>
                    <p>{{ msg('Connect with Stripe for payment processing') }}</p>
                  </div>
                </div>
                <div class="step-form" v-if="branchData">
                  <div class="form-section">
                    <div class="section-title">
                      <feather-icon icon="CreditCardIcon" size="18"/>
                      <h5>{{ msg('Stripe Connect') }}</h5>
                      <b-badge :variant="stripeIsConnected ? 'light-success' : 'light-warning'" class="ml-auto">
                        {{ stripeIsConnected ? msg('Connected') : msg('Not Connected') }}
                      </b-badge>
                    </div>
                    <p class="text-muted small mb-2">{{ msg('Click the button below to connect your Stripe account. You will be redirected to Stripe to register or log in. Once completed, the connection will be applied automatically to all your buildings.') }}</p>
                    <div class="connect-button-row mb-2">
                      <b-button variant="primary" size="lg" @click="connectStripe" :disabled="stripeConnecting || !hasBuildings || stripeIsConnected" class="stripe-connect-btn">
                        <b-spinner small v-if="stripeConnecting" class="mr-50"/>
                        <feather-icon v-else icon="CreditCardIcon" size="20" class="mr-50"/>
                        {{ stripeIsConnected ? msg('Already Connected') : msg('Connect with Stripe') }}
                      </b-button>
                    </div>
                    <b-alert v-if="!hasBuildings" show variant="warning" class="stripe-info-alert mt-1">
                      <feather-icon icon="AlertCircleIcon" size="18" class="mr-1"/>
                      {{ msg('Create at least one building first. Stripe connection is applied per building.') }}
                    </b-alert>
                    <b-alert v-else-if="stripeIsConnected" show variant="success" class="stripe-info-alert mt-1">
                      <feather-icon icon="CheckCircleIcon" size="18" class="mr-1"/>
                      {{ msg('Stripe is connected and applied to your buildings.') }}
                    </b-alert>
                    <span v-else class="connect-hint">{{ msg('You will be redirected to Stripe to complete the setup. After finishing, you will return here automatically.') }}</span>
                  </div>
                  <!-- Building Stripe overview -->
                  <div v-if="wizardBuildings.length > 0" class="form-section mt-2">
                    <div class="section-title">
                      <feather-icon icon="HomeIcon" size="18"/>
                      <h5>{{ msg('Stripe per Building') }}</h5>
                    </div>
                    <p class="text-muted small mb-1">{{ msg('Each building can have its own Stripe account, or use the branch-level Connect account above.') }}</p>
                    <div class="offer-building-list">
                      <div v-for="b in wizardBuildings" :key="b.id" class="offer-building-row mb-1 p-1 border rounded">
                        <div class="d-flex align-items-center mb-75">
                          <span class="font-weight-bold">{{ b.company || b.address || ('#' + b.id) }}</span>
                          <b-badge :variant="(b.stripeApiKey || b.stripeConnectAccountId) ? 'light-success' : 'light-secondary'" pill class="ml-auto">
                            {{ (b.stripeApiKey || b.stripeConnectAccountId) ? msg('Connected') : msg('No Stripe') }}
                          </b-badge>
                        </div>
                        <b-row v-if="b.stripeApiKey || b.stripeConnectAccountId" class="mb-50">
                          <b-col v-if="b.stripeApiKey" cols="12" md="4">
                            <small class="text-muted d-block">{{ msg('Secret Key') }}</small>
                            <span class="small">{{ b.stripeApiKey.substring(0, 12) }}...{{ b.stripeApiKey.slice(-4) }}</span>
                          </b-col>
                          <b-col v-if="b.stripePublicApiKey" cols="12" md="4">
                            <small class="text-muted d-block">{{ msg('Public Key') }}</small>
                            <span class="small">{{ b.stripePublicApiKey.substring(0, 12) }}...{{ b.stripePublicApiKey.slice(-4) }}</span>
                          </b-col>
                          <b-col v-if="b.stripeConnectAccountId" cols="12" md="4">
                            <small class="text-muted d-block">{{ msg('Connect Account') }}</small>
                            <span class="small">{{ b.stripeConnectAccountId }}</span>
                          </b-col>
                        </b-row>
                        <b-button v-if="!b.stripeConnectAccountId" variant="outline-primary" size="sm" @click="connectStripeForBuilding(b)" :disabled="buildingStripeConnecting[b.id]">
                          <b-spinner small v-if="buildingStripeConnecting[b.id]" class="mr-25"/>
                          <feather-icon v-else icon="CreditCardIcon" size="14" class="mr-25"/>
                          {{ msg('Connect Stripe for this Building') }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 6: Communication -->
              <div v-else-if="currentStep === 6" key="communication" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="MessageSquareIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Communication Channels') }}</h4>
                    <p>{{ msg('Set up SMS, phone calls, and WhatsApp messaging') }}</p>
                  </div>
                </div>
                <div class="step-form" v-if="branchData">
                  <!-- Twilio Section -->
                  <div class="form-section">
                    <div class="section-title">
                      <div class="provider-badge twilio-badge">Twilio</div>
                      <b-badge :variant="setupStatus && setupStatus.twillioConnected ? 'light-success' : 'light-warning'" class="ml-auto">
                        {{ setupStatus && setupStatus.twillioConnected ? msg('Connected') : msg('Not Connected') }}
                      </b-badge>
                    </div>
                    <b-row>
                      <b-col cols="12" md="6">
                        <b-form-group :label="msg('Twilio Account SID')">
                          <b-form-input v-model="branchData.twillioId" :placeholder="msg('ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx')"/>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" md="6">
                        <b-form-group :label="msg('Twilio Auth Token')">
                          <b-input-group>
                            <b-form-input :type="showTwilioSecret ? 'text' : 'password'" v-model="branchData.twillioSecret" :placeholder="msg('Your auth token')"/>
                            <b-input-group-append is-text>
                              <feather-icon :icon="showTwilioSecret ? 'EyeOffIcon' : 'EyeIcon'" size="16" class="cursor-pointer" @click="showTwilioSecret = !showTwilioSecret"/>
                            </b-input-group-append>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" md="6">
                        <b-form-group :label="msg('Phone Sender Number')">
                          <b-input-group>
                            <b-input-group-prepend is-text>
                              <feather-icon icon="PhoneIcon" size="16"/>
                            </b-input-group-prepend>
                            <b-form-input v-model="branchData.twillioPhoneSender" :placeholder="msg('+1234567890')"/>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" md="6">
                        <b-form-group :label="msg('WhatsApp Sender Number')">
                          <b-input-group>
                            <b-input-group-prepend is-text>
                              <feather-icon icon="MessageCircleIcon" size="16" style="color: #25D366"/>
                            </b-input-group-prepend>
                            <b-form-input v-model="branchData.twillioWhatsappSender" :placeholder="msg('+1234567890')"/>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>

                  <!-- Twilio WhatsApp Section (WhatsApp via Twilio) -->
                  <div class="form-section">
                    <div class="section-title">
                      <div class="provider-badge whatsapp-badge">
                        <feather-icon icon="MessageCircleIcon" size="16"/>
                        {{ msg('WhatsApp (via Twilio)') }}
                      </div>
                      <b-badge :variant="setupStatus && setupStatus.facebookWhatsappConnected ? 'light-success' : 'light-warning'" class="ml-auto">
                        {{ setupStatus && setupStatus.facebookWhatsappConnected ? msg('Connected') : msg('Not Connected') }}
                      </b-badge>
                    </div>
                    <p class="text-muted small mb-2">{{ msg('WhatsApp is powered by Twilio. Use the button below to create a subaccount and optionally buy a phone number. Main account credentials from branch 1 are used.') }}</p>
                    <div class="connect-button-row mb-2">
                      <b-button variant="success" size="lg" @click="setupTwilioWhatsApp" :disabled="twilioSetupLoading || twilioFieldsFilled" class="whatsapp-connect-btn">
                        <b-spinner small v-if="twilioSetupLoading" class="mr-50"/>
                        <feather-icon v-else icon="ZapIcon" size="20" class="mr-50"/>
                        {{ twilioFieldsFilled ? msg('Already Configured') : msg('Setup Twilio & WhatsApp') }}
                      </b-button>
                    </div>
                    <span class="connect-hint">{{ msg('Creates subaccount, fetches tokens, and optionally purchases a phone number.') }}</span>
                  </div>
                </div>
              </div>

              <!-- Step 7: Buildings -->
              <div v-else-if="currentStep === 7" key="buildings" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="HomeIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Buildings') }}</h4>
                    <p>{{ msg('Manage your buildings and set them as active') }}</p>
                  </div>
                </div>
                <div class="step-form">
                  <div class="quick-stats">
                    <div class="stat-card">
                      <div class="stat-value">{{ setupStatus ? setupStatus.totalBuildings : 0 }}</div>
                      <div class="stat-label">{{ msg('Total') }}</div>
                    </div>
                    <div class="stat-card success">
                      <div class="stat-value">{{ setupStatus ? setupStatus.activeBuildingsCount : 0 }}</div>
                      <div class="stat-label">{{ msg('Active') }}</div>
                    </div>
                  </div>

                  <b-alert show variant="info" class="mt-2">
                    <div class="d-flex align-items-center">
                      <feather-icon icon="InfoIcon" size="18" class="mr-1"/>
                      <div>
                        <strong>{{ msg('How to set up buildings:') }}</strong>
                        <ol class="mb-0 mt-50">
                          <li>{{ msg('Go to Buildings page and create or edit buildings') }}</li>
                          <li>{{ msg('Set the building as Active') }}</li>
                          <li>{{ msg('Assign properties to the building') }}</li>
                        </ol>
                      </div>
                    </div>
                  </b-alert>

                  <div class="action-buttons mt-2">
                    <b-button variant="primary" @click="goToBuildings">
                      <feather-icon icon="ExternalLinkIcon" size="16" class="mr-50"/>
                      {{ msg('Go to Buildings') }}
                    </b-button>
                    <b-button variant="outline-secondary" class="ml-1" @click="refreshStatus">
                      <feather-icon icon="RefreshCwIcon" size="16" class="mr-50"/>
                      {{ msg('Refresh Status') }}
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Step 8: Properties -->
              <div v-else-if="currentStep === 8" key="properties" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="GridIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Properties (Real Estate)') }}</h4>
                    <p>{{ msg('Create properties and link them to buildings. Properties also act as rooms.') }}</p>
                  </div>
                </div>
                <div class="step-form">
                  <div class="quick-stats">
                    <div class="stat-card">
                      <div class="stat-value">{{ setupStatus ? setupStatus.totalRealEstates : 0 }}</div>
                      <div class="stat-label">{{ msg('Total') }}</div>
                    </div>
                    <div class="stat-card success">
                      <div class="stat-value">{{ setupStatus ? setupStatus.activeRealEstatesCount : 0 }}</div>
                      <div class="stat-label">{{ msg('Active') }}</div>
                    </div>
                    <div class="stat-card" :class="setupStatus && setupStatus.realEstatesLinkedToBuildings ? 'success' : 'warning'">
                      <div class="stat-value">{{ setupStatus ? setupStatus.linkedRealEstatesCount : 0 }}</div>
                      <div class="stat-label">{{ msg('Linked') }}</div>
                    </div>
                  </div>

                  <!-- Inline create form when no properties exist -->
                  <template v-if="setupStatus && (setupStatus.totalRealEstates || 0) === 0">
                    <b-alert show variant="warning" class="mt-2 mb-2">
                      <feather-icon icon="AlertCircleIcon" size="18" class="mr-1"/>
                      {{ msg('No properties found. Create your first property below and link it to a building.') }}
                    </b-alert>

                    <div class="form-section">
                      <div class="section-title">
                        <h5>{{ msg('Create First Property') }}</h5>
                      </div>
                      <b-row>
                        <b-col cols="12" md="6" lg="4">
                          <b-form-group :label="msg('Building')">
                            <v-select
                                v-model="newRealEstate.buildingId"
                                :options="wizardBuildings"
                                :label="'company'"
                                :reduce="b => b.id"
                                class="w-100"
                                :placeholder="msg('Select building')"
                            >
                              <template #option="b">{{ b.company || b.address || ('#' + b.id) }}</template>
                              <template #selected-option="b">{{ b.company || b.address || ('#' + b.id) }}</template>
                            </v-select>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <b-form-group :label="msg('Name')">
                            <b-form-input v-model="newRealEstate.name" :placeholder="msg('e.g. Suite 101')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <b-form-group :label="msg('City')">
                            <b-form-input v-model="newRealEstate.city" :placeholder="msg('City')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Zip')">
                            <b-form-input v-model="newRealEstate.zip" type="number" :placeholder="msg('Zip')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('House number')">
                            <b-form-input v-model="newRealEstate.house" type="number" :placeholder="msg('House nr')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Apt')">
                            <b-form-input v-model="newRealEstate.apt" type="number" :placeholder="msg('Apt nr')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Rooms')">
                            <b-form-input v-model="newRealEstate.realEstateArea.rooms" type="number" min="0" step="1" :placeholder="msg('Rooms')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Bathrooms')">
                            <b-form-input v-model="newRealEstate.realEstateArea.bathrooms" type="number" min="0" step="1" :placeholder="msg('Bathrooms')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Guests')">
                            <b-form-input v-model="newRealEstate.peoples" type="number" min="0" step="1" :placeholder="msg('Max guests')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Total space (m²)')">
                            <b-form-input v-model="newRealEstate.realEstateArea.totalSpace" type="number" min="0" step="0.1" :placeholder="msg('m²')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Base price / night')">
                            <b-form-input v-model="newRealEstate.realEstatePrices.basePricePerNight" type="number" step="0.5" :placeholder="msg('Price')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Monthly rent')">
                            <b-form-input v-model="newRealEstate.realEstatePrices.rentTotal" type="number" step="0.5" :placeholder="msg('Rent')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Cleaning fee')">
                            <b-form-input v-model="newRealEstate.realEstatePrices.cleaningFee" type="number" min="0" step="0.1" :placeholder="msg('Fee')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Check-in time')">
                            <v-select
                                v-model="newRealEstate.checkInTime"
                                :options="wizardTimeOptions"
                                label="label"
                                :reduce="t => t.value"
                                :clearable="false"
                                class="w-100"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Check-out time')">
                            <v-select
                                v-model="newRealEstate.checkOutTime"
                                :options="wizardTimeOptions"
                                label="label"
                                :reduce="t => t.value"
                                :clearable="false"
                                class="w-100"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Minimum stay (nights)')">
                            <b-form-input v-model="newRealEstate.minimumStay" type="number" min="1" step="1" :placeholder="msg('Min nights')"/>
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Usage type')">
                            <v-select
                                v-model="newRealEstate.usageType"
                                :options="[
                                  {value:{business:false,investment:false,living:true,waz:false},label:msg('Living')},
                                  {value:{business:true,investment:false,living:false,waz:false},label:msg('Business')},
                                  {value:{business:false,investment:true,living:false,waz:false},label:msg('Investment')}
                                ]"
                                label="label"
                                :reduce="o => o.value"
                                class="w-100"
                                :placeholder="msg('Select usage')"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" lg="3">
                          <b-form-group :label="msg('Type')">
                            <v-select
                                v-model="newRealEstate.marketingType"
                                :options="[
                                  {value:{purchase:false,rent:true},label:msg('Rent')},
                                  {value:{purchase:true,rent:false},label:msg('Purchase')}
                                ]"
                                label="label"
                                :reduce="o => o.value"
                                class="w-100"
                                :placeholder="msg('Rent or Purchase')"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                      <div class="action-buttons mt-1">
                        <b-button variant="primary" @click="createFirstRealEstate" :disabled="creatingRealEstate || !canCreateRealEstate">
                          <b-spinner v-if="creatingRealEstate" small class="mr-50"/>
                          <feather-icon v-else icon="PlusIcon" size="16" class="mr-50"/>
                          {{ msg('Create Property') }}
                        </b-button>
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <b-alert show variant="success" class="mt-2">
                      <feather-icon icon="CheckCircleIcon" size="18" class="mr-1"/>
                      {{ msg('You have properties set up. You can manage them from the Real Estate page.') }}
                    </b-alert>
                  </template>

                  <div class="action-buttons mt-2">
                    <b-button variant="primary" @click="goToRealEstates">
                      <feather-icon icon="ExternalLinkIcon" size="16" class="mr-50"/>
                      {{ msg('Go to Properties') }}
                    </b-button>
                    <b-button variant="outline-secondary" class="ml-1" @click="refreshStatus">
                      <feather-icon icon="RefreshCwIcon" size="16" class="mr-50"/>
                      {{ msg('Refresh Status') }}
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Step 9: Review -->
              <div v-else-if="currentStep === 9" key="review" class="step-panel">
                <div class="step-header">
                  <feather-icon icon="CheckSquareIcon" size="22" class="step-icon"/>
                  <div>
                    <h4>{{ msg('Final Review') }}</h4>
                    <p>{{ msg('Review your setup before finishing') }}</p>
                  </div>
                </div>
                <div class="step-form">
                  <!-- Final Checklist Summary -->
                  <div class="review-checklist">
                    <h5 class="mb-1">{{ msg('Setup Summary') }}</h5>
                    <div v-for="item in checklist" :key="item.id" class="review-item">
                      <feather-icon :icon="item.completed ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                                    size="20"
                                    :class="item.completed ? 'text-success' : 'text-warning'"/>
                      <span class="review-label" :class="{ 'text-muted': item.completed }">{{ msg(item.label) }}</span>
                      <b-badge :variant="item.completed ? 'light-success' : 'light-warning'" pill>
                        {{ item.completed ? msg('Done') : msg('Pending') }}
                      </b-badge>
                      <b-button v-if="!item.completed && item.step <= maxReachableStep" variant="flat-primary" size="sm" class="ml-auto" @click="jumpToStep(item.step)">
                        {{ msg('Fix') }}
                      </b-button>
                      <b-badge v-else-if="!item.completed" variant="light-secondary" pill class="ml-auto">
                        <feather-icon icon="LockIcon" size="12" class="mr-25"/>
                        {{ msg('Complete previous steps first') }}
                      </b-badge>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Bottom Navigation -->
          <div class="wizard-footer">
            <b-button variant="outline-secondary" :disabled="currentStep === 0" @click="prevStep">
              <feather-icon icon="ChevronLeftIcon" size="16" class="mr-25"/>
              {{ msg('Previous') }}
            </b-button>
            <div class="footer-center">
              <b-button variant="primary" @click="saveAndContinue" :disabled="saving">
                <b-spinner small v-if="saving" class="mr-50"/>
                <feather-icon v-else icon="SaveIcon" size="16" class="mr-50"/>
                {{ msg('Save') }}
              </b-button>
            </div>
            <b-button v-if="currentStep < steps.length - 1" variant="primary" @click="nextStep" :disabled="!isStepCompleted(currentStep)">
              {{ msg('Next') }}
              <feather-icon icon="ChevronRightIcon" size="16" class="ml-25"/>
            </b-button>
            <b-button v-else variant="success" @click="finishSetup">
              <feather-icon icon="CheckIcon" size="16" class="mr-50"/>
              {{ msg('Finish Setup') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else class="wizard-loading">
      <b-spinner variant="primary" label="Loading..."/>
      <p class="mt-1">{{ msg('Loading setup wizard...') }}</p>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations} from 'vuex'
import ClientPicker from '@/components/components/picker/clientPicker.vue'
import api from '@/services/api.service'

export default {
  name: 'BranchSetupWizard',
  components: { ClientPicker },
    data() {
    return {
      saving: false,
      creatingBuilding: false,
      stripeConnecting: false,
      buildingStripeConnecting: {},
      whatsappConnecting: false,
      twilioSetupLoading: false,
      showChannelSecret: false,
      showTwilioSecret: false,
      showFbToken: false,
      steps: [
        {label: 'Branch Info', icon: 'BuildingIcon'},
        {label: 'Create Owner', icon: 'UserIcon'},
        {label: 'First Building', icon: 'HomeIcon'},
        {label: 'Check-in Agreement', icon: 'FileTextIcon'},
        {label: 'Channel Manager', icon: 'LinkIcon'},
        {label: 'Stripe', icon: 'CreditCardIcon'},
        {label: 'Communication', icon: 'MessageSquareIcon'},
        {label: 'Buildings', icon: 'HomeIcon'},
        {label: 'Properties', icon: 'GridIcon'},
        {label: 'Final Review', icon: 'CheckSquareIcon'},
      ],
      wizardBuildings: [],
      rentOfferTemplates: [],
      buildingOfferSelections: {},
      bulkOfferTemplateId: null,
      offerStepLoading: false,
      savingOffers: false,
      ownerClient: {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        mobile: '',
      },
      creatingOwner: false,
      firstBuilding: {
        ownerId: null,
        company: '',
        address: '',
        zip: '',
        location: '',
        gst: '',
        kg: '',
      },
      currencyOptions: [
        {value: 'USD', label: 'USD - Dollar'},
        {value: 'EUR', label: 'EUR - Euro'},
        {value: 'ILS', label: 'ILS - Shekel'},
        {value: 'GBP', label: 'GBP - Pound'},
      ],
      creatingRealEstate: false,
      newRealEstate: {
        name: '',
        buildingId: null,
        house: null,
        apt: null,
        city: '',
        zip: null,
        buid: null,
        usageType: {business:false,investment:false,living:true,waz:false},
        marketingType: {purchase:false,rent:true},
        realEstatePrices: { rentTotal: null, basePricePerNight: null, cleaningFee: null },
        realEstateArea: { rooms: null, bathrooms: null, livingSpace: null, totalSpace: null },
        peoples: null,
        checkInTime: '15:00',
        checkOutTime: '11:00',
        minimumStay: 1,
      },
      wizardTimeOptions: [
        {label:'00:00',value:'00:00'},{label:'00:30',value:'00:30'},
        {label:'01:00',value:'01:00'},{label:'01:30',value:'01:30'},
        {label:'02:00',value:'02:00'},{label:'02:30',value:'02:30'},
        {label:'03:00',value:'03:00'},{label:'03:30',value:'03:30'},
        {label:'04:00',value:'04:00'},{label:'04:30',value:'04:30'},
        {label:'05:00',value:'05:00'},{label:'05:30',value:'05:30'},
        {label:'06:00',value:'06:00'},{label:'06:30',value:'06:30'},
        {label:'07:00',value:'07:00'},{label:'07:30',value:'07:30'},
        {label:'08:00',value:'08:00'},{label:'08:30',value:'08:30'},
        {label:'09:00',value:'09:00'},{label:'09:30',value:'09:30'},
        {label:'10:00',value:'10:00'},{label:'10:30',value:'10:30'},
        {label:'11:00',value:'11:00'},{label:'11:30',value:'11:30'},
        {label:'12:00',value:'12:00'},{label:'12:30',value:'12:30'},
        {label:'13:00',value:'13:00'},{label:'13:30',value:'13:30'},
        {label:'14:00',value:'14:00'},{label:'14:30',value:'14:30'},
        {label:'15:00',value:'15:00'},{label:'15:30',value:'15:30'},
        {label:'16:00',value:'16:00'},{label:'16:30',value:'16:30'},
        {label:'17:00',value:'17:00'},{label:'17:30',value:'17:30'},
        {label:'18:00',value:'18:00'},{label:'18:30',value:'18:30'},
        {label:'19:00',value:'19:00'},{label:'19:30',value:'19:30'},
        {label:'20:00',value:'20:00'},{label:'20:30',value:'20:30'},
        {label:'21:00',value:'21:00'},{label:'21:30',value:'21:30'},
        {label:'22:00',value:'22:00'},{label:'22:30',value:'22:30'},
        {label:'23:00',value:'23:00'},{label:'23:30',value:'23:30'},
      ],
    }
  },
  computed: {
    ...mapGetters('setupWizard', ['getSetupStatus', 'isLoading', 'getCurrentStep', 'getBranchId', 'getBranchData', 'getCompletionPercentage', 'getChecklist']),
    ...mapGetters('branches', ['allBranches']),
    ...mapGetters('appConfig', ['isRTL']),
    branchId() {
      return parseInt(this.$route.params.id) || this.getBranchId;
    },
    hasBuildings() {
      return this.setupStatus && (this.setupStatus.totalBuildings || 0) > 0;
    },
    canCreateOwnerClient() {
      const o = this.ownerClient;
      return o && (o.firstname || o.lastname) && (o.phone || o.mobile);
    },
    canCreateFirstBuilding() {
      const b = this.firstBuilding;
      return b && b.ownerId && b.company && b.address && b.gst && b.kg;
    },
    stripeIsConnected() {
      return this.setupStatus && (this.setupStatus.stripeConnectConnected || this.setupStatus.stripeConnected);
    },
    twilioFieldsFilled() {
      const b = this.branchData;
      if (!b) return false;
      return !!(b.twillioId && b.twillioSecret && b.twillioPhoneSender && b.twillioWhatsappSender);
    },
    maxReachableStep() {
      for (let i = 0; i < this.steps.length - 1; i++) {
        if (!this.isStepCompleted(i)) return i;
      }
      return this.steps.length - 1;
    },
    canCreateRealEstate() {
      const r = this.newRealEstate;
      return r.buildingId && r.name && r.house && r.apt && r.city && r.zip
          && r.realEstateArea.rooms && r.realEstateArea.bathrooms && r.peoples
          && r.realEstateArea.totalSpace && r.realEstatePrices.basePricePerNight
          && r.realEstatePrices.rentTotal && r.realEstatePrices.cleaningFee
          && r.checkInTime && r.checkOutTime && r.minimumStay
          && r.usageType && r.marketingType;
    },
    branchData: {
      get() { return this.getBranchData; },
      set(val) { this.setBranchData(val); }
    },
    branchExtData() {
      if (!this.branchData) return {};
      if (!this.branchData.branchExt) this.$set(this.branchData, 'branchExt', {});
      return this.branchData.branchExt;
    },
    setupStatus() { return this.getSetupStatus; },
    loading() { return this.isLoading; },
    currentStep: {
      get() { return this.getCurrentStep; },
      set(val) { this.setStep(val); }
    },
    completionPercentage() { return this.getCompletionPercentage; },
    checklist() { return this.getChecklist; },
  },
  created() {
    this.$store.dispatch('branches/loadData').catch(() => {});
    if (this.branchId) {
      this.$store.commit('data/setSelectedBranches', [this.branchId]);
      this.initWizard(this.branchId).then(() => {
        this.currentStep = this.maxReachableStep;
      });
    }
    this.handleOAuthReturn();
  },
  watch: {
    branchId: {
      handler(bid) {
        if (bid) this.$store.commit('data/setSelectedBranches', [bid]);
      }
    },
    currentStep(val) {
      if (val === 3) this.loadOfferStepData();
      if (val === 5) this.loadStripeBuildingsData();
      if (val === 8) this.loadPropertiesStepData();
    },
    '$route.query'() {
      this.handleOAuthReturn();
    }
  },
  methods: {
    ...mapActions('setupWizard', ['initWizard', 'fetchSetupStatus', 'loadBranch', 'saveBranch', 'setStep', 'createStripeConnectLink', 'createBuildingStripeConnectLink', 'applyStripeConnectToBuildings', 'getWhatsAppAuthUrl']),
    ...mapActions('building', ['getAllBuildings']),
    ...mapActions('realEstate', { addRealEstate: 'addRealEstate' }),
    ...mapActions('contractTemplate', ['getAllTemplates']),
    ...mapMutations('setupWizard', ['setBranchData']),
    jumpToStep(step) {
      if (step === this.steps.length - 1 || step <= this.maxReachableStep) {
        this.currentStep = step;
      }
    },
    nextStep() {
      const next = this.currentStep + 1;
      if (next < this.steps.length && next <= this.maxReachableStep) {
        this.currentStep = next;
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep = this.currentStep - 1;
      }
    },
    isStepCompleted(index) {
      if (!this.setupStatus) return false;
      const s = this.setupStatus;
      switch (index) {
        case 0: return s.branchInfoComplete;
        case 1: return s.hasOwnerClients;
        case 2: return (s.totalBuildings || 0) > 0;
        case 3: return !!s.offerTemplatesConfigured;
        case 4: return s.externalApiConnected;
        case 5: return s.stripeConnected || s.stripeConnectConnected;
        case 6: return s.twillioConnected && s.facebookWhatsappConnected;
        case 7: return s.hasBuildingsActive;
        case 8: return s.hasRealEstatesActive && s.realEstatesLinkedToBuildings;
        case 9: return true;
        default: return false;
      }
    },
    async saveAndContinue() {
      if (!this.branchData || !this.branchId) return;
      this.saving = true;
      try {
        await this.saveBranch({id: this.branchId, branch: this.branchData});
        await this.fetchSetupStatus(this.branchId);
      } finally {
        this.saving = false;
      }
    },
    async refreshStatus() {
      await this.fetchSetupStatus(this.branchId);
    },
    async loadOfferStepData() {
      if (!this.branchId) return;
      this.offerStepLoading = true;
      try {
        this.$store.commit('data/setSelectedBranches', [this.branchId]);
        const emptyFilter = { eq: {}, like: {}, lte: {}, gte: {}, in: {} };
        const [bAns, tAns] = await Promise.all([
          this.getAllBuildings({
            page: 1,
            amount: 200,
            filter: emptyFilter,
            sortcolumn: 'company',
            sortby: 'asc',
          }),
          this.getAllTemplates({
            page: 1,
            amount: 200,
            filter: { eq: { template: true, status: 'RENT_OFFER' }, like: {}, lte: {}, gte: {}, in: {} },
            sortcolumn: 'name',
            sortby: 'asc',
          }),
        ]);
        const bid = Number(this.branchId);
        const buildings = (bAns.Buildings || []).filter(b => Number(b.buid) === bid);
        this.wizardBuildings = buildings;
        this.rentOfferTemplates = (tAns.Templates || []).filter(t => t != null);
        const sel = {};
        buildings.forEach(b => {
          const ids = Array.isArray(b.offerTemplateIds) ? [...b.offerTemplateIds] : [];
          this.$set(sel, b.id, ids);
        });
        this.buildingOfferSelections = sel;
      } catch (e) {
        this.$store.dispatch('notifications/error', {
          title: this.msg('Setup Wizard'),
          message: (e && e.message) || this.msg('Failed to load buildings or templates'),
        }, { root: true });
      } finally {
        this.offerStepLoading = false;
      }
    },
    applyBulkOfferTemplate() {
      if (!this.bulkOfferTemplateId) return;
      this.wizardBuildings.forEach(b => {
        this.$set(this.buildingOfferSelections, b.id, [this.bulkOfferTemplateId]);
      });
    },
    async saveOfferTemplateAssignments() {
      if (!this.branchId || this.wizardBuildings.length === 0) return;
      const hasAnySelection = this.wizardBuildings.some(b => {
        const ids = this.buildingOfferSelections[b.id];
        return ids && ids.length > 0;
      });
      if (!hasAnySelection) {
        this.$store.dispatch('notifications/error', {
          title: this.msg('Setup Wizard'),
          message: this.msg('Please select at least one check-in agreement template before saving'),
        }, { root: true });
        return;
      }
      this.savingOffers = true;
      try {
        for (const b of this.wizardBuildings) {
          const ids = this.buildingOfferSelections[b.id] || [];
          if (ids.length === 0) continue;
          await api.sendRequest('building/update', null, { ...b, offerTemplateIds: ids });
        }
        await this.$store.dispatch('notifications/success', {
          title: this.msg('Setup Wizard'),
          message: this.msg('Offer templates saved'),
        }, { root: true });
        await this.fetchSetupStatus(this.branchId);
      } catch (e) {
        const m = (e && e.message) || this.msg('Failed to save');
        await this.$store.dispatch('notifications/error', { title: this.msg('Setup Wizard'), message: m }, { root: true });
      } finally {
        this.savingOffers = false;
      }
    },
    goToNewOfferTemplate() {
      const href = this.$router.resolve({ path: '/real_estate/contracts/template/new/' + this.branchId, query: { status: 'RENT_OFFER' } }).href;
      window.open(href, '_blank');
    },
    goToOfferTemplatesList() {
      const href = this.$router.resolve({ path: '/real_estate/contracts/templates' }).href;
      window.open(href, '_blank');
    },
    async loadStripeBuildingsData() {
      if (!this.branchId) return;
      try {
        this.$store.commit('data/setSelectedBranches', [this.branchId]);
        const bAns = await this.getAllBuildings({
          page: 1, amount: 200,
          filter: { eq: {}, like: {}, lte: {}, gte: {}, in: {} },
          sortcolumn: 'company', sortby: 'asc',
        });
        const bid = Number(this.branchId);
        this.wizardBuildings = (bAns.Buildings || []).filter(b => Number(b.buid) === bid);
      } catch (e) { /* ignore */ }
    },
    async connectStripeForBuilding(building) {
      if (!this.branchId || !building.id) return;
      this.$set(this.buildingStripeConnecting, building.id, true);
      try {
        const url = await this.createBuildingStripeConnectLink({ branchId: this.branchId, buildingId: building.id });
        if (url) {
          const stripeWindow = window.open(url, '_blank');
          const poll = setInterval(async () => {
            if (stripeWindow && stripeWindow.closed) {
              clearInterval(poll);
              this.$set(this.buildingStripeConnecting, building.id, false);
              await this.loadStripeBuildingsData();
              await this.fetchSetupStatus(this.branchId);
            }
          }, 1500);
        } else {
          this.$set(this.buildingStripeConnecting, building.id, false);
        }
      } catch (e) {
        this.$set(this.buildingStripeConnecting, building.id, false);
      }
    },
    async loadPropertiesStepData() {
      if (!this.branchId) return;
      try {
        this.$store.commit('data/setSelectedBranches', [this.branchId]);
        const bAns = await this.getAllBuildings({
          page: 1, amount: 200,
          filter: { eq: {}, like: {}, lte: {}, gte: {}, in: {} },
          sortcolumn: 'company', sortby: 'asc',
        });
        const bid = Number(this.branchId);
        this.wizardBuildings = (bAns.Buildings || []).filter(b => Number(b.buid) === bid);
        this.newRealEstate.buid = bid;
      } catch (e) { /* buildings already loaded from offer step */ }
    },
    async createFirstRealEstate() {
      if (!this.canCreateRealEstate || !this.branchId) return;
      this.creatingRealEstate = true;
      try {
        const re = { ...this.newRealEstate, buid: this.branchId };
        await this.addRealEstate(re);
        await this.fetchSetupStatus(this.branchId);
        this.newRealEstate = {
          name: '', buildingId: null, house: null, apt: null, city: '', zip: null, buid: this.branchId,
          usageType: {business:false,investment:false,living:true,waz:false},
          marketingType: {purchase:false,rent:true},
          realEstatePrices: { rentTotal: null, basePricePerNight: null, cleaningFee: null },
          realEstateArea: { rooms: null, bathrooms: null, livingSpace: null, totalSpace: null },
          peoples: null, checkInTime: '15:00', checkOutTime: '11:00', minimumStay: 1,
        };
      } catch (e) {
        this.$store.dispatch('notifications/error', { title: 'Setup Wizard', message: e.message || 'Failed to create property' }, { root: true });
      } finally {
        this.creatingRealEstate = false;
      }
    },
    goToBuildings() {
      this.$router.push('/buildings');
    },
    goToRealEstates() {
      this.$router.push('/real_estate');
    },
    finishSetup() {
      this.$router.push('/organization/branches');
    },
    async connectStripe() {
      if (!this.branchId) return;
      this.stripeConnecting = true;
      try {
        const url = await this.createStripeConnectLink(this.branchId);
        if (url) {
          const stripeWindow = window.open(url, '_blank');
          const poll = setInterval(async () => {
            if (stripeWindow && stripeWindow.closed) {
              clearInterval(poll);
              this.stripeConnecting = false;
              await this.fetchSetupStatus(this.branchId);
            }
          }, 1500);
        } else {
          this.stripeConnecting = false;
        }
      } catch (e) {
        this.stripeConnecting = false;
      }
    },
    async connectWhatsApp() {
      if (!this.branchId) return;
      this.whatsappConnecting = true;
      try {
        const url = await this.getWhatsAppAuthUrl(this.branchId);
        if (url) window.location.href = url;
      } finally {
        this.whatsappConnecting = false;
      }
    },
    async setupTwilioWhatsApp() {
      if (!this.branchId) return;
      this.twilioSetupLoading = true;
      try {
        const ans = await this.$store.dispatch('setupWizard/setupTwilio', { branchId: this.branchId });
        if (ans && ans.branch) {
          this.$store.commit('setupWizard/setBranchData', ans.branch);
          await this.fetchSetupStatus(this.branchId);
          this.$store.dispatch('notifications/success', { title: 'Twilio', message: 'Twilio & WhatsApp configured successfully' }, { root: true });
        }
      } catch (e) {
        const msg = typeof e === 'string' ? e : (e?.response?.data?.message || e?.message) || 'Failed to setup Twilio';
        this.$store.dispatch('notifications/error', { title: 'Twilio Setup', message: msg }, { root: true });
      } finally {
        this.twilioSetupLoading = false;
      }
    },
    onBranchChange(bid) {
      if (bid && bid !== this.branchId) {
        this.$router.push('/organization/branches/setup/' + bid);
        this.initWizard(bid).then(() => {
          this.currentStep = this.maxReachableStep;
        });
      }
    },
    async createOwnerClient() {
      if (!this.canCreateOwnerClient || !this.branchId) return;
      this.creatingOwner = true;
      try {
        const client = {
          branchId: this.branchId,
          firstname: this.ownerClient.firstname || '',
          lastname: this.ownerClient.lastname || '',
          email: this.ownerClient.email || null,
          phone: this.ownerClient.phone || this.ownerClient.mobile || '',
          mobile: this.ownerClient.mobile || this.ownerClient.phone || null,
          clientExt: { isOwner: true, isRenter: false },
        };
        await this.$store.dispatch('clients/register', { client });
        await this.fetchSetupStatus(this.branchId);
        this.ownerClient = { firstname: '', lastname: '', email: '', phone: '', mobile: '' };
      } catch (e) {
        this.$store.dispatch('notifications/error', { title: 'Setup Wizard', message: e.message || 'Failed to create owner client' }, { root: true });
      } finally {
        this.creatingOwner = false;
      }
    },
    async createFirstBuilding() {
      if (!this.canCreateFirstBuilding || !this.branchId) return;
      this.creatingBuilding = true;
      try {
        const building = {
          buid: this.branchId,
          ownerId: this.firstBuilding.ownerId,
          company: this.firstBuilding.company,
          address: this.firstBuilding.address,
          zip: this.firstBuilding.zip || null,
          location: this.firstBuilding.location || null,
          gst: this.firstBuilding.gst,
          kg: this.firstBuilding.kg,
          active: true,
        };
        await this.$store.dispatch('building/addBuilding', building);
        await this.fetchSetupStatus(this.branchId);
        this.loadOfferStepData();
        this.firstBuilding = { ownerId: null, company: '', address: '', zip: '', location: '', gst: '', kg: '' };
      } catch (e) {
        this.$store.dispatch('notifications/error', { title: 'Setup Wizard', message: e.message || 'Failed to create building' }, { root: true });
      } finally {
        this.creatingBuilding = false;
      }
    },
    handleOAuthReturn() {
      const q = this.$route.query;
      if (q.stripe === 'success' || q.stripe === 'refresh') {
        this.$router.replace({path: this.$route.path, query: {}});
        if (q.stripe === 'success') {
          this.applyStripeConnectToBuildings(this.branchId).then((count) => {
            const msg = count > 0 ? `Stripe connected. Applied to ${count} building(s).` : 'Stripe connected successfully.';
            this.$store.dispatch('notifications/success', {title: 'Stripe', message: msg}, {root: true});
          }).catch(() => {}).finally(() => {
            this.fetchSetupStatus(this.branchId);
            if (window.opener) window.close();
          });
        } else {
          this.fetchSetupStatus(this.branchId);
          if (window.opener) window.close();
        }
      }
      if (q.whatsapp === 'success') {
        this.fetchSetupStatus(this.branchId);
        this.loadBranch(this.branchId);
        this.$router.replace({path: this.$route.path, query: {}});
        this.$store.dispatch('notifications/success', {title: 'WhatsApp', message: 'WhatsApp connected successfully'}, {root: true});
      } else if (q.whatsapp && q.whatsapp !== 'success') {
        this.$router.replace({path: this.$route.path, query: {}});
        this.$store.dispatch('notifications/error', {title: 'WhatsApp', message: 'Could not connect WhatsApp. Please try again.'}, {root: true});
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.setup-wizard-page {
  min-height: 100vh;
  background: #f8f9fb;
}

/* Header Banner */
.wizard-header-banner {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  padding: 1.5rem 2rem;
  color: white;
  box-shadow: 0 4px 24px rgba(115, 103, 240, 0.25);
}
.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
}
.banner-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.banner-icon-wrap {
  width: 48px;
  height: 48px;
  background: rgba(255,255,255,0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.banner-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
  color: white;
}
.banner-subtitle {
  margin: 0;
  opacity: 0.85;
  font-size: 0.9rem;
}
.banner-right {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}
.branch-picker-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.branch-picker-label {
  font-size: 0.75rem;
  opacity: 0.9;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.branch-picker-select {
  min-width: 280px;
  max-width: 360px;
}
.branch-picker-select ::v-deep .vs__dropdown-toggle {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.35);
  color: white;
}
.branch-picker-select ::v-deep .vs__dropdown-menu {
  background: rgba(100, 90, 200, 0.98);
  border-color: rgba(255, 255, 255, 0.2);
}
.branch-picker-select ::v-deep .vs__dropdown-option--highlight {
  background: rgba(255, 255, 255, 0.25);
}
.branch-picker-select ::v-deep .vs__dropdown-option {
  color: white;
}
.completion-ring {
  width: 56px;
  height: 56px;
}
.circular-chart {
  display: block;
  max-width: 100%;
  max-height: 100%;
}
.circle-bg {
  fill: none;
  stroke: rgba(255,255,255,0.2);
  stroke-width: 3.8;
}
.circle {
  fill: none;
  stroke: white;
  stroke-width: 3.8;
  stroke-linecap: round;
  transition: stroke-dasharray 0.6s ease;
}
.percentage {
  fill: white;
  font-size: 0.5em;
  text-anchor: middle;
  font-weight: 700;
}
.completion-label {
  font-size: 0.85rem;
  opacity: 0.9;
}

/* Body */
.wizard-body {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
}
.wizard-layout {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

/* Sidebar */
.wizard-sidebar {
  width: 320px;
  flex-shrink: 0;
  position: sticky;
  top: 1.5rem;
}
.sidebar-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);
  overflow: hidden;
}
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eff0f2;
  h5 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
  }
}
.checklist-items {
  padding: 0.5rem 0;
}
.checklist-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  &:hover {
    background: #f8f8fa;
  }
  &.active {
    background: rgba(115, 103, 240, 0.04);
    border-left-color: #7367f0;
  }
  &.completed {
    .check-label { color: #6e6b7b; }
  }
  &.locked {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover { background: transparent; }
  }
}
.check-indicator {
  flex-shrink: 0;
}
.check-done {
  color: #28c76f;
}
.check-pending {
  color: #d4d4d8;
}
.check-content {
  flex: 1;
  min-width: 0;
}
.check-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: #3b3f5c;
}
.check-desc {
  display: block;
  font-size: 0.75rem;
  color: #a3a3a3;
  margin-top: 1px;
}
.check-arrow {
  color: #d4d4d8;
  flex-shrink: 0;
}

/* Main Content */
.wizard-main {
  flex: 1;
  min-width: 0;
}

/* Step Tabs */
.step-tabs {
  display: flex;
  background: white;
  border-radius: 12px;
  padding: 0.5rem;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);
  gap: 0.25rem;
  overflow-x: auto;
}
.step-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  &:hover {
    background: #f8f8fa;
  }
  &.active {
    background: #7367f0;
    color: white;
    .step-number { background: rgba(255,255,255,0.2); color: white; }
  }
  &.completed:not(.active) {
    .step-number { background: #e8f5e9; color: #28c76f; }
  }
  &.locked {
    opacity: 0.35;
    cursor: not-allowed;
    &:hover { background: transparent; }
  }
}
.step-number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f3f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}
.step-label {
  font-size: 0.82rem;
  font-weight: 500;
}

/* Step Content */
.step-content-area {
  margin-top: 1.25rem;
}
.step-panel {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);
  padding: 1.75rem;
}
.step-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #eff0f2;
  h4 {
    margin: 0;
    font-weight: 600;
    font-size: 1.15rem;
  }
  p {
    margin: 0.2rem 0 0 0;
    color: #a3a3a3;
    font-size: 0.85rem;
  }
}
.step-icon {
  color: #7367f0;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Form Sections */
.form-section {
  margin-bottom: 1.75rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f3f3f5;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}
.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  h5 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
  }
}
.provider-badge {
  padding: 0.3rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
}
.twilio-badge {
  background: linear-gradient(135deg, #F22F46 0%, #e81833 100%);
}
.fb-badge {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
}
.whatsapp-badge {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
}
.stripe-info-alert {
  border-radius: 8px;
  font-size: 0.85rem;
}

/* Quick Stats */
.quick-stats {
  display: flex;
  gap: 1rem;
}
.stat-card {
  background: #f8f9fb;
  border-radius: 10px;
  padding: 1rem 1.5rem;
  text-align: center;
  min-width: 100px;
  border: 1px solid #eff0f2;
  &.success {
    background: #e8f5e9;
    border-color: #c8e6c9;
    .stat-value { color: #28c76f; }
  }
  &.warning {
    background: #fff8e1;
    border-color: #ffecb3;
    .stat-value { color: #ff9f43; }
  }
}
.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #3b3f5c;
}
.stat-label {
  font-size: 0.75rem;
  color: #a3a3a3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

/* Review Checklist */
.review-checklist {
  background: #f8f9fb;
  border-radius: 10px;
  padding: 1.25rem;
}
.review-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #eff0f2;
  &:last-child { border-bottom: none; }
}
.review-label {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Footer */
.wizard-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.25rem;
  padding: 1rem 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);
}
.footer-center {
  display: flex;
  gap: 0.5rem;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter {
  transform: translateX(20px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* Loading */
.wizard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6e6b7b;
}

/* Responsive */
@media (max-width: 991px) {
  .wizard-layout {
    flex-direction: column;
  }
  .wizard-sidebar {
    width: 100%;
    position: static;
  }
  .step-tabs {
    overflow-x: auto;
  }
}
@media (max-width: 768px) {
  .banner-content { flex-wrap: wrap; gap: 1rem; }
  .banner-right { flex-wrap: wrap; }
}
@media (max-width: 576px) {
  .wizard-header-banner { padding: 1rem; }
  .banner-title { font-size: 1.1rem; }
  .branch-picker-select { min-width: 200px; max-width: 260px; }
  .step-panel { padding: 1.25rem; }
  .step-label { display: none; }
}

/* Dark mode support */
.dark-layout {
  .setup-wizard-page { background: #161d31; }
  .sidebar-card, .step-panel, .step-tabs, .wizard-footer { background: #283046; box-shadow: 0 4px 24px rgba(0,0,0,0.2); }
  .sidebar-header { border-color: #3b4253; }
  .checklist-item {
    &:hover { background: #323d55; }
    &.active { background: rgba(115, 103, 240, 0.12); }
  }
  .check-label { color: #d0d2d6; }
  .step-tab:hover { background: #323d55; }
  .step-tab.locked:hover { background: transparent; }
  .checklist-item.locked:hover { background: transparent; }
  .step-number { background: #3b4253; }
  .step-header { border-color: #3b4253; }
  .form-section { border-color: #3b4253; }
  .stat-card { background: #323d55; border-color: #3b4253; .stat-value { color: #d0d2d6; } }
  .review-checklist { background: #323d55; }
  .review-item { border-color: #3b4253; }
}

/* Connect buttons */
.connect-button-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.stripe-connect-btn {
  background: linear-gradient(135deg, #635bff 0%, #7c3aed 100%) !important;
  border: none;
}
.whatsapp-connect-btn {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%) !important;
  border: none;
}
.connect-hint {
  font-size: 0.85rem;
  color: #a3a3a3;
}

/* Utility */
.cursor-pointer { cursor: pointer; }

.offer-building-list .offer-building-row {
  background: rgba(115, 103, 240, 0.06);
}
.dark-layout .offer-building-list .offer-building-row {
  background: rgba(115, 103, 240, 0.08);
  border-color: #3b4253 !important;
}
</style>
